/**
 * Shadow Stalker Item — Plan 4b Task 7.1
 *
 * Spec §4 #11: Thin wrapper around Plan 5's spawnShadowStalker.
 * Delegates spawn + single-stalker enforcement to Plan 5.
 * Emits item_cast + npc_summoned (Plan 4b breadcrumb) in addition to Plan 5's npc_spawned.
 *
 * Self-registers via addItem(shadowStalkerItem) at module load.
 */
import type { GameState } from '../types';
import type { ItemCastCtx, ItemCastResult, Item } from './types';
import { appendEvent } from '../event-log';
import { spawnShadowStalker } from '../npc/shadow-stalker';
import { addItem } from './registry';

export const shadowStalkerItem: Item = {
  id: 'shadow_stalker',
  name: 'Shadow Stalker',
  description:
    'Summon a Shadow Stalker NPC that hunts other players (not the summoner). ' +
    'Only one Shadow Stalker can exist at a time — summoning replaces any existing stalker.',
  targetType: 'self',
  category: 'complex',
  rarity: 'rare',
  blockedByStoneSkin: false, // self-cast — Stone Skin rule 8

  cast(state: GameState, ctx: ItemCastCtx): ItemCastResult {
    const { casterId } = ctx;

    // Step 1: Validate caster (spawnShadowStalker throws if missing)
    // Step 2: Delegate to Plan 5 (handles single-stalker enforcement + npc_spawned)
    const npc = spawnShadowStalker(state, casterId);

    // Step 3: Emit Plan 4b breadcrumb events
    appendEvent(state, {
      type: 'item_cast',
      playerId: casterId,
      casterId,
      itemId: 'shadow_stalker',
    });
    appendEvent(state, {
      type: 'npc_summoned',
      playerId: casterId,
      casterId,
      itemId: 'shadow_stalker',
      npcId: npc.id,
    });

    return { success: true, endsTurn: false };
  },
};

// Self-register at module load
addItem(shadowStalkerItem);
